import axios, { AxiosInstance } from 'axios';
import { CalendarSlot, mapBackendSlotToFrontend } from '../types/calendar.types';
import { API_BASE_URL } from '../config/api.config';

// API Base URL - zentrale Konfiguration
const API_URL = API_BASE_URL;

// Helper function to format date without timezone conversion
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Terminanfrage zu geplanten Terminen (Befund B7)
export interface SlotInquiryRequest {
  name: string;
  email: string;
  phone?: string;
  participantCount: number;
  message?: string;
  website?: string; // Honeypot, bleibt leer
}

export interface SlotInquiryResult {
  success: boolean;
  message: string;
}

export interface CalendarAvailability {
  isAvailable: boolean;
  availableCapacity: number;
  waitlistAvailable: boolean;
  message?: string;
  suggestedAlternatives?: CalendarSlot[];
}

// NEW: Schedule-based capacity management types
export interface CalendarSlotGroup {
  scheduleId: number;
  date: string;
  startTime: string;
  totalCapacity: number;
  currentBookings: number;
  availableCapacity: number;
  capacityPercentage: number;
  minParticipants: number;
  isConfirmed: boolean;
  status: string;
  isAvailable: boolean;
  colorCode: string;
  slots: CalendarSlotDetail[];
}

export interface CalendarSlotDetail {
  id: number;
  tourType: string;
  pricePerPerson: number;
  currentBookings: number;
  specialNotes?: string;
  isAvailable: boolean;
  isBlocked: boolean;
}

export interface CreateCalendarSlotDto {
  date: string;
  time: string;
  tourType: string;
  maxCapacity: number;
  minCapacity: number;
  pricePerPerson: number;
  specialNotes?: string;
}

export interface BulkCreateCalendarSlotDto {
  startDate: string;
  endDate: string;
  weekDays: number[]; // 0=Sunday, 1=Monday, etc.
  times: string[];
  tourType: string;
  maxCapacity: number;
  minCapacity: number;
  pricePerPerson: number;
  excludeDates?: string[];
}

export class CalendarService {
  private static axiosInstance: AxiosInstance = CalendarService.createAxiosInstance();

  private static createAxiosInstance(): AxiosInstance {
    // T-161 (v1.8.46, Session 40): ohne die Request-Header Cache-Control, Pragma und Expires. Sie lösten bei Aufrufen von
    // https://www.frankenfloss.net einen CORS-Preflight aus, den der Server ablehnt ("Fehler beim Laden der Termine" bei
    // "Angebot anlegen", Kundin 02.10.2026). Gegen Browser-Caches genügt der Parameter _t der Abrufe.
    const instance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Auth-Token mitsenden, wenn vorhanden (B7): Admin/Staff bekommen so im Kalender
    // weiterhin die ungefilterte Sicht (/calendar/grouped filtert für anonyme Aufrufer).
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers = config.headers || {};
        (config.headers as any)['Authorization'] = `Bearer ${token}`;
      }
      return config;
    });

    return instance;
  }

  /**
   * Get color code based on availability percentage
   */
  private static getColorCode(availableCapacity: number, maxCapacity: number): string {
    const percentage = (availableCapacity / maxCapacity) * 100;
    
    if (percentage > 50) return '#4ade80'; // green-400
    if (percentage > 20) return '#facc15'; // yellow-400
    return '#f87171'; // red-400
  }

  /**
   * Get available calendar slots for a date range.
   * includePlanned=true (Befund B7): liefert zusätzlich geplante, noch nicht zur
   * Online-Buchung freigegebene Termine (isReleasedForOnlineBooking === false).
   */
  static async getAvailableSlots(from: Date, to: Date, tourType?: string, includePlanned?: boolean): Promise<CalendarSlot[]> {
    try {
      const params: any = {
        from: formatDateLocal(from),
        to: formatDateLocal(to),
        _t: Date.now(), // Cache buster
      };

      if (tourType) {
        params.tourType = tourType;
      }

      if (includePlanned) {
        params.includePlanned = true;
      }

      console.log('CalendarService: Fetching slots from API with params:', params);
      const response = await this.axiosInstance.get('/calendar/slots', { params });
      console.log('CalendarService: Received response:', response.data?.length || 0, 'slots');

      // Add color codes to the response
      const slots: CalendarSlot[] = response.data.map((slot: CalendarSlot) => ({
        ...slot,
        colorCode: this.getColorCode(slot.availableCapacity, slot.maxCapacity)
      }));

      return slots;
    } catch (error) {
      console.error('Error fetching calendar slots:', error);
      throw error;
    }
  }

  /**
   * Terminanfrage zu einem geplanten, noch nicht freigegebenen Termin senden (Befund B7).
   * Erzeugt keine Buchung, sondern eine E-Mail an das Frankenfloß-Team.
   */
  static async submitSlotInquiry(slotId: number, inquiry: SlotInquiryRequest): Promise<SlotInquiryResult> {
    try {
      const response = await this.axiosInstance.post(`/calendar/slots/${slotId}/inquiry`, inquiry);
      return response.data as SlotInquiryResult;
    } catch (error: any) {
      const data = error?.response?.data;
      if (data && typeof data.message === 'string') {
        return { success: false, message: data.message };
      }
      console.error('Error submitting slot inquiry:', error);
      return {
        success: false,
        message: 'Ihre Anfrage konnte nicht übermittelt werden. Bitte versuchen Sie es später erneut.'
      };
    }
  }

  /**
   * Check availability for a specific date, time and participant count
   */  static async checkAvailability(
    date: string, 
    time: string, 
    participants: number,
    tourType?: string
  ): Promise<CalendarAvailability> {
    try {
      const params: any = { date, time, participants };
      if (tourType) {
        params.tourType = tourType;
      }

      const response = await this.axiosInstance.get('/calendar/availability', { params });
      return response.data;
    } catch (error) {
      console.error('Error checking availability:', error);
      throw error;
    }
  }

  /**
   * Get calendar slots for a specific date
   */
  static async getSlotsForDate(date: string): Promise<CalendarSlot[]> {
    try {
      const response = await this.axiosInstance.get(`/calendar/date/${date}`, {
        params: { _t: Date.now() } // Cache buster
      });

      // Add color codes to the response
      const slots: CalendarSlot[] = response.data.map((slot: CalendarSlot) => ({
        ...slot,
        colorCode: this.getColorCode(slot.availableCapacity, slot.maxCapacity)
      }));

      return slots;
    } catch (error) {
      console.error('Error fetching slots for date:', error);
      throw error;
    }
  }

  /**
   * NEW: Get calendar slots grouped by Schedule (physical boat trips)
   * Implements schedule-based capacity management
   */
  static async getGroupedSlots(from: Date, to: Date, tourType?: string): Promise<CalendarSlotGroup[]> {
    try {
      const params: any = {
        from: formatDateLocal(from),
        to: formatDateLocal(to),
        _t: Date.now(), // Cache buster
      };

      if (tourType) {
        params.tourType = tourType;
      }

      console.log('CalendarService: Fetching grouped slots from API with params:', params);
      const response = await this.axiosInstance.get('/calendar/grouped', { params });
      console.log('CalendarService: Received grouped response:', response.data?.length || 0, 'groups');

      return response.data;
    } catch (error) {
      console.error('Error fetching grouped calendar slots:', error);
      throw error;
    }
  }

  /**
   * Create a new calendar slot (Admin only)
   */
  static async createSlot(slot: CreateCalendarSlotDto): Promise<CalendarSlot> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.axiosInstance.post('/calendar', slot, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating calendar slot:', error);
      throw error;
    }
  }
  /**
   * Bulk create calendar slots (Admin only)
   */
  static async bulkCreateSlots(data: BulkCreateCalendarSlotDto): Promise<CalendarSlot[]> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.axiosInstance.post('/calendar/bulk', data, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk creating calendar slots:', error);
      throw error;
    }
  }

  /**
   * Update a calendar slot (Admin only)
   */
  static async updateSlot(id: number, updates: Partial<CalendarSlot>): Promise<CalendarSlot> {
    try {
      const token = localStorage.getItem('authToken');
      const response = await this.axiosInstance.put(`/calendar/${id}`, updates, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating calendar slot:', error);
      throw error;
    }
  }

  /**
   * Block or unblock a calendar slot (Admin only)
   */
  static async toggleBlockSlot(id: number, block: boolean, reason?: string): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      await this.axiosInstance.post(
        `/calendar/${id}/block`,
        { block, reason },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
    } catch (error) {
      console.error('Error toggling slot block:', error);
      throw error;
    }
  }

  /**
   * Delete a calendar slot (Admin only)
   */
  static async deleteSlot(id: number): Promise<void> {
    try {
      const token = localStorage.getItem('authToken');
      await this.axiosInstance.delete(`/calendar/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Error deleting calendar slot:', error);
      throw error;
    }
  }
}

export default CalendarService;